import { useEffect, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { Loader2 } from "lucide-react";
import { useAppointments } from "../appointments.hooks";
import { toast } from "sonner";
import { FormCreator, FormFieldDef } from "@/components/shared/form-creator";
import {
  fetchVendorsForParent,
  fetchChildServices,
  fetchPropertiesForVendor,
  fetchUnits,
  fetchParentCategories,
  createUnit,
} from "../appointments.api";
import { CreateAppointmentPayload } from "../appointments.entity";
import { PropertyUnit } from "@/models/properties.model";
import { uuid } from "@/interfaces";
import UnitCreationModal from "@/app/vendors/appointments/components/UnitCreationModal";
import { bookingRefusalMessage, isBookableDay } from "@/lib/appointment-booking";
import { calendarDay } from "@/lib/appointment-reschedule";

const bookingSchema = z.object({
  parentService: z.string().min(1, "Service category is required"),
  vendor: z.string().min(1, "Vendor is required"),
  childService: z.string().min(1, "Service is required"),
  property: z.string().min(1, "Property is required"),
  unit: z.string().min(1, "Unit is required"),
  date: z.date({ required_error: "Date is required" }),
  work_notes: z.string().optional(),
});

type BookingFormValues = z.infer<typeof bookingSchema>;

export default function BookingSheet() {
  const { sheetOpen, closeSheet, create } = useAppointments();

  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  // Options state
  const [parentChoices, setParentChoices] = useState<string[]>([]);
  const [vendors, setVendors] = useState<{ id: string; name: string }[]>([]);
  const [childList, setChildList] = useState<{ id: string; name: string }[]>([]);
  const [properties, setProperties] = useState<{ id: string; name: string }[]>([]);
  const [units, setUnits] = useState<{ id: string; unit_number: string }[]>([]);

  // Unit creation modal
  const [unitModalOpen, setUnitModalOpen] = useState(false);

  const form = useForm<BookingFormValues>({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      parentService: "",
      vendor: "",
      childService: "",
      property: "",
      unit: "",
      date: undefined,
      work_notes: "",
    },
  });

  const watchedValues = form.watch();

  // Fetch parent categories when sheet opens
  useEffect(() => {
    if (!sheetOpen) return;
    fetchParentCategories().then(setParentChoices).catch(console.error);
  }, [sheetOpen]);

  // Fetch vendors when parent service changes
  useEffect(() => {
    form.setValue("vendor", "");
    form.setValue("childService", "");
    setVendors([]);
    setChildList([]);
    if (!watchedValues.parentService) return;
    fetchVendorsForParent(watchedValues.parentService).then(setVendors).catch(console.error);
  }, [watchedValues.parentService]);

  // Fetch child services when vendor changes
  useEffect(() => {
    form.setValue("childService", "");
    setChildList([]);
    if (!watchedValues.vendor || !watchedValues.parentService) return;
    fetchChildServices(watchedValues.vendor, watchedValues.parentService).then(setChildList).catch(console.error);
  }, [watchedValues.vendor, watchedValues.parentService]);

  // Fetch properties when vendor changes
  useEffect(() => {
    form.setValue("property", "");
    setProperties([]);
    if (!watchedValues.vendor) return;

    fetchPropertiesForVendor(watchedValues.vendor).then(setProperties).catch(console.error);
  }, [watchedValues.vendor]);

  // Fetch units when property changes
  useEffect(() => {
    form.setValue("unit", "");
    setUnits([]);
    if (!watchedValues.property) return;
    fetchUnits(watchedValues.property).then(setUnits).catch(console.error);
  }, [watchedValues.property]);

  const resetAll = () => {
    setStep(1);
    form.reset();
    setVendors([]);
    setChildList([]);
    setProperties([]);
    setUnits([]);
  };

  const handleUnitCreated = async (unitData: Omit<PropertyUnit, "id" | "created_at" | "updated_at" | "deleted_at">) => {
    try {
      const created = await createUnit(unitData);
      toast.success("Unit created successfully");
      setUnitModalOpen(false);
      if (watchedValues.property) {
        setUnits(await fetchUnits(watchedValues.property));
        form.setValue("unit", created.id);
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to create unit");
    }
  };

  const confirm = async (closeAfter: boolean) => {
    const isValid = await form.trigger();
    if (!isValid) {
      toast.error("Please fill in all required fields");
      return;
    }

    const values = form.getValues();
    const payload: CreateAppointmentPayload = {
      property: values.property,
      unit: values.unit,
      vendor: values.vendor,
      service: values.childService,
      date: calendarDay(values.date),
      work_order_notes: values.work_notes || undefined,
    };

    setSaving(true);
    try {
      await create(payload);
      toast.success("Appointment booked");
      if (closeAfter) {
        closeSheet();
        resetAll();
      } else {
        resetAll();
      }
    } catch (error) {
      toast.error(bookingRefusalMessage(error));
    } finally {
      setSaving(false);
    }
  };

  // Form definition with dynamic options
  const formDefinition: FormFieldDef[] = useMemo(
    () => [
      {
        type: "section",
        name: "service-section",
        label: "Service Details",
      },
      {
        type: "combobox",
        name: "parentService",
        label: "",
        placeholder: "Service category",
        options: parentChoices.map((p) => ({ label: p, value: p })),
        required: true,
      },
      {
        type: "combobox",
        name: "vendor",
        label: "",
        placeholder: "Vendor",
        options: vendors.map((v) => ({ label: v.name, value: v.id })),
        disabledWhen: (values) => !values.parentService,
        required: true,
      },
      {
        type: "combobox",
        name: "childService",
        label: "",
        placeholder: "Specific service",
        options: childList.map((s) => ({ label: s.name, value: s.id })),
        disabledWhen: (values) => !values.vendor,
        required: true,
      },
      {
        type: "section",
        name: "property-section",
        label: "Property Details",
      },
      {
        type: "combobox",
        name: "property",
        label: "",
        placeholder: "Property",
        options: properties.map((p) => ({ label: p.name, value: p.id })),
        disabledWhen: (values) => !values.vendor,
        required: true,
      },
      {
        type: "combobox",
        name: "unit",
        label: "",
        placeholder: "Unit",
        options: units.map((u) => ({ label: u.unit_number, value: u.id })),
        disabledWhen: (values) => !values.property,
        required: true,
        addonRight: (values: any) => (
          <Button
            type="button"
            variant="outline"
            onClick={() => setUnitModalOpen(true)}
            disabled={!values.property}
          >
            Add Unit
          </Button>
        ),
      },
      {
        type: "date",
        name: "date",
        label: "",
        placeholder: "Pick a date",
        isDisabledDay: (day: Date) => !isBookableDay(day),
        required: true,
      },
      {
        type: "textarea",
        name: "work_notes",
        label: "",
        placeholder: "Work Notes (optional)",
      },
    ],
    [parentChoices, vendors, childList, properties, units]
  );

  const canProceed =
    watchedValues.parentService &&
    watchedValues.vendor &&
    watchedValues.childService &&
    watchedValues.property &&
    watchedValues.unit &&
    watchedValues.date;

  return (
    <>
      <Sheet
        open={sheetOpen}
        onOpenChange={() => {
          closeSheet();
          resetAll();
        }}
      >
        <SheetContent className="w-full sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>New Appointment</SheetTitle>
            <SheetDescription>
              {step === 1 && "Select service, vendor, property, unit and date"}
              {step === 2 && "Review then save"}
            </SheetDescription>
          </SheetHeader>

          <div className="p-4 space-y-6">
            {step === 1 && (
              <FormCreator
                form={form}
                onSubmit={() => setStep(2)}
                formDefinition={formDefinition}
                formClassName="space-y-4"
                renderButtons={() => (
                  <div className="flex justify-end gap-4 pt-4">
                    <Button disabled={!canProceed} onClick={() => setStep(2)}>
                      Next
                    </Button>
                  </div>
                )}
              />
            )}

            {step === 2 && (
              <>
                <ul className="space-y-1 text-sm">
                  <li>
                    <strong>Category:</strong> {watchedValues.parentService}
                  </li>
                  <li>
                    <strong>Service:</strong> {childList.find((c) => c.id === watchedValues.childService)?.name}
                  </li>
                  <li>
                    <strong>Vendor:</strong> {vendors.find((v) => v.id === watchedValues.vendor)?.name}
                  </li>
                  <li>
                    <strong>Property:</strong> {properties.find((p) => p.id === watchedValues.property)?.name}
                  </li>
                  <li>
                    <strong>Unit:</strong> {units.find((u) => u.id === watchedValues.unit)?.unit_number}
                  </li>
                  <li>
                    <strong>Date:</strong> {watchedValues.date ? format(watchedValues.date, "PPP") : "—"}
                  </li>
                  <li>
                    <strong>Work Notes:</strong> {watchedValues.work_notes || "—"}
                  </li>
                </ul>

                <div className="flex justify-end gap-4 pt-4">
                  <Button variant="outline" onClick={() => setStep(1)} disabled={saving}>
                    Back
                  </Button>
                  <Button variant="secondary" onClick={() => confirm(false)} disabled={saving}>
                    {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save & Book Another
                  </Button>
                  <Button onClick={() => confirm(true)} disabled={saving}>
                    {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save & Close
                  </Button>
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <UnitCreationModal
        open={unitModalOpen}
        onOpenChange={setUnitModalOpen}
        propertyId={watchedValues.property as uuid}
        onSubmit={handleUnitCreated}
      />
    </>
  );
}
